<template>
    <CollapsibleCard
        class="conversation-v2 border-gray"
        :collapsed="collapsed"
        @update:collapsed="$emit('update:collapsed', $event)"
    >
        <template #title>
            <h5 class="mb-0">
                {{ resolvedTitle }}
            </h5>
        </template>

        <template #actions>
            <BButton
                v-if="isOpen"
                variant="flat-secondary"
                size="sm"
                :disabled="isLoading"
                @click="isOpen = false"
            >
                {{ resolvedCloseLabel }}
            </BButton>
        </template>

        <div v-if="isLoading" class="conversation-v2__summary text-muted">
            <BSpinner small class="mr-50" />
            {{ resolvedLoadingLabel }}
        </div>

        <BAlert v-else-if="error" variant="danger" show class="mb-0">
            <div class="alert-body">
                {{ resolvedErrorLabel }}
                <BButton class="ml-50" size="sm" variant="outline-danger" @click="load">
                    {{ resolvedRetryLabel }}
                </BButton>
            </div>
        </BAlert>

        <template v-else-if="isOpen">
            <div class="conversation-v2__thread" :style="threadStyle">
                <ConversationThread
                    v-if="messages.length > 0"
                    :messages="messages"
                    :subscriber-id="subscriber.id"
                />
                <p v-else class="mb-0 text-muted text-center">
                    {{ resolvedNoMessagesLabel }}
                </p>
            </div>

            <form class="conversation-v2__composer" @submit.prevent="send">
                <textarea
                    v-model="draft"
                    class="form-control"
                    :disabled="isSending"
                    :placeholder="resolvedMessagePlaceholder"
                    rows="1"
                    wrap="soft"
                />
                <BButton
                    class="mt-50"
                    variant="primary"
                    type="submit"
                    block
                    :disabled="!canSend"
                >
                    <BSpinner v-if="isSending" small class="mr-50" />
                    {{ resolvedSendLabel }}
                </BButton>
            </form>
        </template>

        <div v-else class="conversation-v2__summary">
            <p class="mb-1 text-body">
                {{ compactDescription }}
            </p>
            <BButton variant="primary" @click="isOpen = true">
                {{ resolvedOpenLabel }}
            </BButton>
        </div>
    </CollapsibleCard>
</template>

<script>
import {
    BAlert,
    BButton,
    BSpinner,
} from 'bootstrap-vue'
import { MessengerApi } from '@core/api'
import CollapsibleCard from '@core/components/common/CollapsibleCard.vue'
import ConversationThread from './ConversationThread.vue'

const defaultChat = () => ({
    id: null,
    subscriber: { id: null, title: '' },
    messages: [],
})

export default {
    name: 'ConversationV2',
    components: {
        BAlert,
        BButton,
        BSpinner,
        CollapsibleCard,
        ConversationThread,
    },
    props: {
        requestId: {
            type: [String, Number],
            required: true,
        },
        collapsed: {
            type: Boolean,
            default: null,
        },
        title: {
            type: String,
            default: null,
        },
        compactDescriptionTemplate: {
            type: String,
            default: null,
        },
        openLabel: {
            type: String,
            default: null,
        },
        closeLabel: {
            type: String,
            default: null,
        },
        sendLabel: {
            type: String,
            default: null,
        },
        loadingLabel: {
            type: String,
            default: null,
        },
        errorLabel: {
            type: String,
            default: null,
        },
        retryLabel: {
            type: String,
            default: null,
        },
        noMessagesLabel: {
            type: String,
            default: null,
        },
        messagePlaceholder: {
            type: String,
            default: null,
        },
        threadHeight: {
            type: Number,
            default: 230,
        },
    },
    data() {
        return {
            chat: defaultChat(),
            draft: '',
            error: null,
            isLoading: false,
            isOpen: false,
            isSending: false,
            loadRevision: 0,
        }
    },
    computed: {
        resolvedTitle() {
            return this.translationOrFallback(
                this.title,
                'Page(ReceivedRequest):Component(Conversation):title',
                'Messages',
            )
        },
        resolvedOpenLabel() {
            return this.translationOrFallback(
                this.openLabel,
                'Component(Conversation):open-messages',
                'Open messages',
            )
        },
        resolvedCloseLabel() {
            return this.translationOrFallback(
                this.closeLabel,
                'Component(Conversation):close-messages',
                'Close messages',
            )
        },
        resolvedSendLabel() {
            return this.translationOrFallback(
                this.sendLabel,
                'Component(Button):send',
                'Send',
            )
        },
        resolvedLoadingLabel() {
            return this.translationOrFallback(
                this.loadingLabel,
                'Component(Conversation):loading',
                'Loading messages…',
            )
        },
        resolvedErrorLabel() {
            return this.translationOrFallback(
                this.errorLabel,
                'Component(Conversation):load-error',
                'Messages could not be loaded.',
            )
        },
        resolvedRetryLabel() {
            return this.translationOrFallback(
                this.retryLabel,
                'Component(Button):retry',
                'Retry',
            )
        },
        resolvedNoMessagesLabel() {
            return this.translationOrFallback(
                this.noMessagesLabel,
                'Page(ReceivedRequest):Component(Conversation):no-messages',
                'No messages yet.',
            )
        },
        resolvedMessagePlaceholder() {
            return this.translationOrFallback(
                this.messagePlaceholder,
                'Page(ReceivedRequest):Component(Conversation):message-placeholder',
                'Write a message',
            )
        },
        messages() {
            return this.chat.messages || []
        },
        subscriber() {
            return this.chat.subscriber || { id: null, title: '' }
        },
        compactDescription() {
            const template = this.translationOrFallback(
                this.compactDescriptionTemplate,
                'Component(Conversation):send-message-to',
                'Send a message to {subscriber}.',
            )
            const subscriber = this.subscriber.title || this.translationOrFallback(
                null,
                'Component(Conversation):supplier-fallback',
                'the supplier',
            )

            return template.replace('{subscriber}', subscriber)
        },
        canSend() {
            return this.draft.trim().length > 0 && !this.isSending
        },
        threadStyle() {
            return { maxHeight: `${this.threadHeight}px` }
        },
    },
    created() {
        this.load()
    },
    watch: {
        requestId() {
            this.load()
        },
    },
    methods: {
        translationOrFallback(value, key, fallback) {
            if (value) {
                return value
            }

            if (typeof this.$t !== 'function') {
                return fallback
            }

            const translated = this.$t(key)

            return translated && translated !== key ? translated : fallback
        },
        async load() {
            const revision = this.loadRevision + 1
            this.loadRevision = revision
            this.error = null
            this.isLoading = true
            this.isOpen = false
            this.chat = defaultChat()

            try {
                const response = await MessengerApi.getChatByRequestId(this.requestId)

                if (revision !== this.loadRevision) {
                    return
                }

                this.chat = {
                    ...defaultChat(),
                    ...response.data,
                    subscriber: {
                        ...defaultChat().subscriber,
                        ...(response.data.subscriber || {}),
                    },
                    messages: Array.isArray(response.data.messages) ? response.data.messages : [],
                }
                this.isOpen = this.messages.length > 0
                if (this.isOpen) {
                    this.$nextTick(this.scrollThreadToBottom)
                }
            } catch (error) {
                if (revision === this.loadRevision) {
                    this.error = error
                }
            } finally {
                if (revision === this.loadRevision) {
                    this.isLoading = false
                }
            }
        },
        async send() {
            if (!this.canSend || !this.chat.id) {
                return
            }

            this.isSending = true
            this.error = null
            try {
                const response = await MessengerApi.sendMessage(this.chat.id, this.draft.trim())
                this.chat.messages.push(response.data)
                this.draft = ''
                this.$nextTick(this.scrollThreadToBottom)
            } catch (error) {
                this.error = error
            } finally {
                this.isSending = false
            }
        },
        scrollThreadToBottom() {
            const thread = this.$el.querySelector('.conversation-v2__thread')

            if (thread) {
                thread.scrollTop = thread.scrollHeight
            }
        },
    },
}
</script>

<style scoped lang="scss">
.conversation-v2__summary,
.conversation-v2__composer {
    padding: 1rem;
}

.conversation-v2__thread {
    padding: 1rem;
    overflow-y: auto;
    background: #fff;
}

.conversation-v2__composer {
    border-top: 1px solid #ebe9f1;
    background: #f8f8f8;
}
</style>
